import {
  Box,
  Button,
  classSet,
  EllipsisMenu,
  SearchInput
} from '@platform/ui-library';
import { FC, useEffect, useMemo, useState } from 'react';
import { Route, Routes, useSearchParams } from 'react-router-dom';
import { KeyCode } from '@app/const';
import {
  useCustomNavigate,
  useLocalSerializedDesigns,
  useServices,
  useUser
} from '@app/hooks';
import {
  CustomNavigate,
  Table,
  VersionSelector
} from '@app/page-components';
import { TimeInMs } from '@app/types';
import { formatDate, formatTimeSince } from '@app/utilities';
import LoadingSpinner from '@app/widgets/components/LoadingSpinner';
import { HomeCreatePage } from './create/page';
import { HomeDownloadPage } from './download/page';
import { HomeFeaturesPage } from './features/page';
import { HomeUploadPage } from './upload/page';
import { ReactComponent as Logo } from '../../assets/interactions-full-logo.svg';
import styles from './layout.module.scss';

const isIFrame = window.top !== window.self;

enum Columns {
  actions = '_Actions',
  client = 'Client',
  design = 'Design',
  branch = 'Branch',
  lastUpdate = 'Last Update',
  version = 'Version'
}

const filterColumns = [
  Columns.client,
  Columns.design,
  Columns.branch,
  Columns.version
];
const sortColumns = [
  ...filterColumns,
  Columns.lastUpdate
];

export const HomeLayout: FC = () => {
  const searchDesignId = useSearchParams()[0].get('designId');
  const {
    globalModalsService,
    lokiService,
    userService
  } = useServices();
  const navigate = useCustomNavigate();
  const [search, setSearch] = useState('');
  const user = useUser();
  const serializedDesigns = useLocalSerializedDesigns();

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      const { code, altKey, ctrlKey } = event;
      if (code === KeyCode.d && altKey && ctrlKey) {
        event.preventDefault();
        navigate('features');
      }
    };

    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, []);

  const rawTableItems = serializedDesigns?.filter(({ designId }) => {
    return !searchDesignId || designId === searchDesignId;
  }).map(({
    branchId,
    branchName,
    clientName,
    designName,
    lastUpdate,
    version
  }) => {
    const lastUpdateContent = (lastUpdate < (Date.now() - TimeInMs.day)) ? (
      formatDate(lastUpdate)
    ) : (
      formatTimeSince(lastUpdate)
    );

    return {
      id: branchId,
      [Columns.client]: clientName,
      [Columns.design]: designName,
      [Columns.branch]: branchName,
      [Columns.version]: {
        content: (
          <VersionSelector
            branchId={branchId}
            onVersionChange={async (serialized) => {
              await lokiService.storeSerialized(serialized);
            }}
            version={version}
          />
        ),
        raw: version
      },
      [Columns.lastUpdate]: {
        content: lastUpdateContent,
        raw: lastUpdate
      },
      [Columns.actions]: (
        <>
          <Button
            autoFocus={false}
            icon="delete"
            type="button"
            theme="outline"
            onClick={async () => {
              if (await globalModalsService.openConfirmationModal({
                children: `Are you sure you want to delete your local copy of "${designName} ${version}"?`,
                labels: {
                  header: 'Delete confirmation',
                  cancelButton: 'Cancel',
                  continueButton: 'Delete'
                }
              })) {
                lokiService.removeSerialized(branchId);
              }
            }}
          />
          <Button
            icon="edit"
            type="button"
            theme="outlineHighlight"
            onClick={() => {
              let route = `/app?g=${branchId}`;
              if (searchDesignId) {
                route += `&designId=${searchDesignId}`;
              }
              location.href = route;
            }}
          />
        </>
      )
    };
  });

  const tableItems = useMemo(() => {
    if (!search) {
      return rawTableItems;
    }
    return rawTableItems?.filter((item) => {
      return filterColumns.some((column) => {
        const columnValue = item[column];
        const raw = (typeof columnValue === 'object' && 'raw' in columnValue) ? (
          columnValue.raw
        ) : columnValue;
        return (
          typeof raw === 'string'
          && raw.toLowerCase().includes(search.toLowerCase())
        );
      });
    });
  }, [search, rawTableItems]);

  if (!user || !userService.isLoggedIn) {
    return (
      <>
        <div className={styles.background} />
        <div className={classSet(['grid-layout', styles.layout])}>
          <div className={styles.header}>
            <Logo className={styles.logo} />
          </div>
        </div>
      </>
    );
  }

  let content: JSX.Element;
  if (!rawTableItems) {
    // Still loading local DB.
    content = (
      <LoadingSpinner message="Loading local design versions..." />
    );
  } else if (!rawTableItems.length) {
    // No items in local DB.
    content = (
      <div className={styles.emptyStateContainer}>
        <p>No local versions were found.</p>
        <div className="flex-center flex-gap">
          <Button
            icon="download"
            onClick={() => {
              navigate('download');
            }}
            type="button"
          >
            Download a design
          </Button>
          {(searchDesignId) ? null : (
            <>
              <Button
                icon="upload"
                onClick={() => {
                  navigate('upload');
                }}
                theme='outline'
                type="button"
              >
                Upload a design
              </Button>
              <Button
                icon="plus"
                onClick={() => {
                  navigate('create');
                }}
                theme='outline'
                type="button"
              >
                Create a design
              </Button>
            </>
          )}
        </div>
      </div>
    );
  } else if (!tableItems?.length) {
    // Search filtered out all items.
    content = (
      <div className={`flex-center ${styles.noResults}`}>No results</div>
    );
  } else {
    content = (
      <Table
        className={styles.table}
        data={tableItems}
        filter={search}
        filterByColumns={filterColumns}
        initialSort={{ desc: true, sort: Columns.lastUpdate }}
        sortByColumns={sortColumns}
      />
    );
  }

  return (
    <>
      <div className={classSet([styles.background, styles.loggedIn])} />
      <div className={classSet(['grid-layout', styles.layout])}>
        <div className={styles.header}>
          <Logo className={styles.logo} />
          {(isIFrame) ? null : (
            <Button
              icon='crossSmall'
              onClick={() => {
                userService.user = null;
              }}
              theme='outline'
              type="button"
            >
              Log Out {user.username}
            </Button>
          )}
        </div>
        <Box className={styles.versionsBox} theme="noPadding">
          <div className={styles.boxHeader}>
            <h2>Local Versions</h2>
            <SearchInput
              name="search"
              placeholder="Search"
              onChange={setSearch}
              value={search}
            />
            {(searchDesignId) ? (
              <Button
                className={styles.addButton}
                icon="plus"
                onClick={() => {
                  navigate('download');
                }}
                type="button"
              />
            ) : (
              <div className="circle-menu">
                <EllipsisMenu
                  icon='plus'
                  items={[
                    {
                      action: () => {
                        navigate('download');
                      },
                      icon: 'download',
                      label: 'Download'
                    },
                    {
                      action: () => {
                        navigate('upload');
                      },
                      icon: 'upload',
                      label: 'Upload'
                    },
                    {
                      action: () => {
                        navigate('create');
                      },
                      icon: 'plus',
                      label: 'Create'
                    }
                  ]}
                  theme='outline'
                />
              </div>
            )}
          </div>
          <div className="box content overflow-visible">
            {content}
          </div>
          <Routes>
            <Route path="download" element={<HomeDownloadPage />} />
            <Route path="features" element={<HomeFeaturesPage />} />
            {(searchDesignId) ? null : (
              <>
                <Route path="create" element={<HomeCreatePage />} />
                <Route path="upload" element={<HomeUploadPage />} />
              </>
            )}
            <Route path="*" element={<CustomNavigate to="../" />} />
          </Routes>
        </Box>
      </div>
    </>
  );
};
